/**
 * Typed surface for the Meta Pixel (`fbq`) global installed by Meta's
 * fbevents bootstrap snippet. Kept separate from React hooks so
 * non-React call sites (`fireConversion`) can import the typed global
 * without dragging in the injection effect.
 *
 * Wire-format identifiers (`"em"`, `"external_id"`, `"PageView"`,
 * `"eventID"`, `"content_ids"`) are Meta's; they're exposed as named
 * constants so call sites write `payload[META_USER_DATA_KEY_EMAIL]`
 * instead of free-form string literals — refactor-safe and lint-clean
 * (no snake_case type properties leak out of this file).
 */

export const FbqCommand = {
  INIT: "init",
  TRACK: "track",
} as const;
export type FbqCommand = (typeof FbqCommand)[keyof typeof FbqCommand];

export const MetaStandardEvent = {
  PAGE_VIEW: "PageView",
  COMPLETE_REGISTRATION: "CompleteRegistration",
  VIEW_CONTENT: "ViewContent",
  INITIATE_CHECKOUT: "InitiateCheckout",
  START_TRIAL: "StartTrial",
  SUBSCRIBE: "Subscribe",
  PURCHASE: "Purchase",
} as const;
export type MetaStandardEvent = (typeof MetaStandardEvent)[keyof typeof MetaStandardEvent];

export const META_PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";
export const META_PIXEL_SCRIPT_ID = "nx-meta-pixel-script";
export const META_PIXEL_SDK_VERSION = "2.0";

export const META_USER_DATA_KEY_EMAIL = "em";
export const META_USER_DATA_KEY_EXTERNAL_ID = "external_id";
export const META_TRACK_OPTION_KEY_EVENT_ID = "eventID";
// Meta standard `track` params that disambiguate `ViewContent` across
// surfaces (pricing page vs. product page vs. article). See
// https://developers.facebook.com/docs/meta-pixel/reference#object-properties
export const META_TRACK_PARAM_KEY_CONTENT_NAME = "content_name";
export const META_TRACK_PARAM_KEY_CONTENT_CATEGORY = "content_category";

export type MetaWireStringPayload = Record<string, string>;
export type MetaWireParams = Record<string, unknown>;
export type MetaWireOptions = Record<string, unknown>;

export interface IFbq {
  (command: typeof FbqCommand.INIT, pixelId: string, userData?: MetaWireStringPayload): void;
  (command: typeof FbqCommand.TRACK, eventName: MetaStandardEvent, params?: MetaWireParams, options?: MetaWireOptions): void;
  callMethod?: (command: string, ...rest: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
  push: IFbq;
}

/* eslint-disable @typescript-eslint/naming-convention -- `Window` is a built-in DOM interface we're augmenting via declaration merging; the `_fbq` underscore prefix is mandated by the Meta fbevents SDK's initialization protocol (it reads `window._fbq` to detect prior installation). Neither name is under our control. */
declare global {
  interface Window {
    fbq?: IFbq;
    _fbq?: IFbq;
  }
}
/* eslint-enable @typescript-eslint/naming-convention -- end of browser-global augmentation block. */
